/** Firm-wide deadlines calendar: returns, extensions, estimated payments, notice responses, payroll, annual reports and sales tax. */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Pager, StatRow, usePaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { formatDate, localToday } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import type { Me } from "@/lib/session";
import { cn } from "@/lib/utils";

type Item = {
  date: string;
  kind: "return" | "extension" | "estimate" | "notice" | "payroll_run" | "payroll_filing" | "entity_filing" | "sales_tax";
  module: "tax" | "payroll" | "bookkeeping";
  clientId: string;
  clientName: string;
  title: string;
  detail: string;
  href: string;
  assignedUserId: string | null;
  done: boolean;
  overdue: boolean;
};

const KINDS: Record<Item["kind"], { label: string; dot: string }> = {
  return: { label: "Returns", dot: "bg-blue-600" },
  extension: { label: "File or extend", dot: "bg-indigo-400" },
  estimate: { label: "Estimated payments", dot: "bg-emerald-600" },
  notice: { label: "Notice responses", dot: "bg-amber-500" },
  payroll_run: { label: "Pay dates", dot: "bg-cyan-600" },
  payroll_filing: { label: "Payroll filings", dot: "bg-fuchsia-600" },
  entity_filing: { label: "Annual reports", dot: "bg-slate-500" },
  sales_tax: { label: "Sales tax", dot: "bg-rose-600" },
};
// Shown in the key only when the month has some (not every firm runs payroll or tracks compliance).
const OPTIONAL_KINDS = new Set<Item["kind"]>(["payroll_run", "payroll_filing", "entity_filing", "sales_tax"]);
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const monthStart = (ym: string) => new Date(`${ym}-01T12:00:00Z`);
function addMonths(ym: string, n: number) {
  const d = monthStart(ym);
  d.setUTCMonth(d.getUTCMonth() + n);
  return iso(d).slice(0, 7);
}

// A day's list stays short until opened.
const DAY_SHOWN = 8;

/** One day of the agenda: the first few deadlines, with "Show all" for a busy day. */
function AgendaDay({ day, list, today, names }: { day: string; list: Item[]; today: string; names: Map<string, string> }) {
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, DAY_SHOWN);
  return (
    <section className="overflow-hidden rounded-lg border border-card-border bg-card">
      <h2 className={cn("border-b border-border bg-secondary/50 px-4 py-2 text-sm font-semibold", day === today && "text-primary")}>
        {formatDate(day)}
        {day === today && " · Today"}
        <span className="ml-2 text-xs font-normal text-muted-foreground">{list.length === 1 ? "1 deadline" : `${list.length} deadlines`}</span>
      </h2>
      <ul className="divide-y divide-border">
        {shown.map((x, i) => (
          <li key={i}>
            <Link href={x.href} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", KINDS[x.kind].dot)} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className={cn("block truncate text-sm", x.done && "text-muted-foreground line-through")}>
                  <span className="font-medium">{x.clientName}</span> · {x.title}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {x.detail}
                  {x.assignedUserId && ` · ${names.get(x.assignedUserId) ?? ""}`}
                </span>
              </span>
              {x.overdue && <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900 dark:bg-red-900/30 dark:text-red-200">Overdue</span>}
              {x.done && <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">Done</span>}
            </Link>
          </li>
        ))}
      </ul>
      {list.length > DAY_SHOWN && (
        <button type="button" onClick={() => setAll((v) => !v)} aria-expanded={all} className="flex w-full items-center gap-1 border-t border-border px-4 py-2 text-sm font-medium text-primary hover:bg-muted/30">
          {all ? "Show fewer" : `Show all ${list.length} deadlines`}
          <ChevronDown className={cn("h-4 w-4 transition-transform", all && "rotate-180")} aria-hidden />
        </button>
      )}
    </section>
  );
}

export function DeadlinesPage({ me }: { me: Me }) {
  const today = localToday();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [kinds, setKinds] = useState<Set<Item["kind"]>>(new Set(Object.keys(KINDS) as Item["kind"][]));
  const [mine, setMine] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const names = useMemberNames();
  // The grid shows whole weeks around the month.
  const first = monthStart(month);
  const gridStart = new Date(first.getTime() - first.getUTCDay() * 86_400_000);
  const last = monthStart(addMonths(month, 1));
  last.setUTCDate(0);
  const gridEnd = new Date(last.getTime() + (6 - last.getUTCDay()) * 86_400_000);
  const { data, isLoading } = useQuery<{ items: Item[] }>({ queryKey: [`/api/deadlines?from=${iso(gridStart)}&to=${iso(gridEnd)}`] });
  // Overdue and the week ahead don't depend on the month shown: a year back to a week ahead.
  const weekAhead = iso(new Date(Date.parse(`${today}T12:00:00Z`) + 7 * 86_400_000));
  const near = useQuery<{ items: Item[] }>({ queryKey: [`/api/deadlines?from=${iso(new Date(Date.parse(`${today}T12:00:00Z`) - 365 * 86_400_000))}&to=${weekAhead}`] });
  const visible = (x: Item) => kinds.has(x.kind) && (!mine || x.assignedUserId === me.user?.id);
  const nearItems = (near.data?.items ?? []).filter(visible);
  const items = useMemo(
    () => (data?.items ?? []).filter((x) => kinds.has(x.kind) && (showDone || !x.done) && (!mine || x.assignedUserId === me.user?.id)),
    [data, kinds, mine, showDone, me.user?.id],
  );
  const byDay = new Map<string, Item[]>();
  for (const x of items) byDay.set(x.date, [...(byDay.get(x.date) ?? []), x]);
  const days: string[] = [];
  for (let d = gridStart.getTime(); d <= gridEnd.getTime(); d += 86_400_000) days.push(iso(new Date(d)));
  const inMonth = items.filter((x) => x.date.startsWith(month));
  // The agenda pages by day: a busy month doesn't become one endless page.
  const agenda = usePaged([...new Set(inMonth.map((x) => x.date))], { resetKey: month });
  const present = new Set((data?.items ?? []).map((x) => x.kind));
  const toggle = (k: Item["kind"]) => setKinds((s) => (s.has(k) ? new Set([...s].filter((x) => x !== k)) : new Set([...s, k])));
  const label = first.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

  return (
    <>
      <PageHeader title="Deadlines" description="Every client deadline across the firm, by date. Each item opens where the work is done." />
      <StatRow
        stats={[
          { label: "Overdue", value: nearItems.filter((x) => x.overdue).length, tone: nearItems.some((x) => x.overdue) ? "danger" : "success" },
          { label: `Due in ${first.toLocaleString("en-US", { month: "long", timeZone: "UTC" })}`, value: inMonth.filter((x) => !x.done).length },
          { label: "Due in the next 7 days", value: nearItems.filter((x) => !x.done && x.date >= today && x.date <= weekAhead).length, tone: "warning" },
          { label: "Done this month", value: (data?.items ?? []).filter((x) => visible(x) && x.done && x.date.startsWith(month)).length, tone: "success" },
        ]}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" aria-label="Previous month" onClick={() => setMonth(addMonths(month, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="w-40 text-center text-sm font-semibold">{label}</span>
          <Button size="sm" variant="outline" aria-label="Next month" onClick={() => setMonth(addMonths(month, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {month !== today.slice(0, 7) && (
            <Button size="sm" variant="ghost" onClick={() => setMonth(today.slice(0, 7))}>
              Today
            </Button>
          )}
        </div>
        <div className="flex basis-full flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:flex-1 sm:basis-0 sm:justify-end">
          {(Object.keys(KINDS) as Item["kind"][])
            .filter((k) => present.has(k) || !OPTIONAL_KINDS.has(k))
            .map((k) => (
              <label key={k} className="flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" checked={kinds.has(k)} onChange={() => toggle(k)} />
                <span className={cn("h-2 w-2 rounded-full", KINDS[k].dot)} aria-hidden />
                {KINDS[k].label}
              </label>
            ))}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Assigned to me
          </label>
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Show done
          </label>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Month grid: tablet and wider. */}
          <div className="mb-6 hidden overflow-hidden rounded-lg border border-card-border bg-card md:block">
            <div className="grid grid-cols-7 border-b border-border bg-secondary/50 text-center text-xs font-medium text-muted-foreground">
              {WEEKDAYS.map((w) => (
                <div key={w} className="py-2">
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d) => {
                const list = byDay.get(d) ?? [];
                const out = !d.startsWith(month);
                return (
                  <div key={d} className={cn("min-h-28 min-w-0 border-b border-r border-border p-1.5 [&:nth-child(7n)]:border-r-0", out && "bg-muted/30")}>
                    <div className={cn("mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs tabular", d === today ? "bg-primary font-semibold text-primary-foreground" : out ? "text-muted-foreground" : "text-muted-foreground")}>{Number(d.slice(8))}</div>
                    <ul className="space-y-0.5">
                      {list.slice(0, 4).map((x, i) => (
                        <li key={i}>
                          <Link href={x.href} title={`${x.clientName}: ${x.title}`} className={cn("flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px] hover:bg-muted", x.done && "text-muted-foreground line-through", x.overdue && "text-red-700 dark:text-red-300")}>
                            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", KINDS[x.kind].dot)} aria-hidden />
                            <span className="truncate">{x.clientName}</span>
                          </Link>
                        </li>
                      ))}
                      {list.length > 4 && <li className="px-1 text-[11px] text-muted-foreground">+{list.length - 4} more</li>}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Agenda: every item this month, grouped by day. */}
          {!inMonth.length ? (
            <Empty>No deadlines in {label} with these filters.</Empty>
          ) : (
            <div className="space-y-4">
              {agenda.items.map((d) => (
                <AgendaDay key={d} day={d} list={byDay.get(d)!} today={today} names={names} />
              ))}
              <Pager paged={agenda} noun="days" />
            </div>
          )}
        </>
      )}
    </>
  );
}
